import React from 'react';
import {
  Modal,
  Button,
  Select,
  DualDateRangePicker,
  Typography,
  Form,
} from '@crisellevaldez/speculo-ui';

type FilterState = {
  status: string[];
  dateRange: { startDate: Date | null; endDate: Date | null };
};

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFilter: (filters: FilterState) => void;
  initialFilters: FilterState;
}

const statusOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'finished', label: 'Finished' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
];

export const ReviveFilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  onFilter,
  initialFilters,
}) => {
  const [status, setStatus] = React.useState<string[]>(initialFilters.status);
  const [dateRange, setDateRange] = React.useState(initialFilters.dateRange);

  // Update state when initialFilters change or when filters are reset
  React.useEffect(() => {
    setStatus(initialFilters.status);
    setDateRange(initialFilters.dateRange);
  }, [initialFilters]);

  // Reset filters when modal is opened
  React.useEffect(() => {
    if (isOpen) {
      setStatus(initialFilters.status);
      setDateRange(initialFilters.dateRange);
    }
  }, [isOpen, initialFilters]);

  const handleReset = () => {
    const emptyFilters = {
      status: [],
      dateRange: { startDate: null, endDate: null },
    };
    setStatus(emptyFilters.status);
    setDateRange(emptyFilters.dateRange);
    onFilter(emptyFilters);
    onClose();
  };

  const handleFilter = () => {
    onFilter({
      status,
      dateRange,
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <Modal.Header>
        <Typography variant="h5">Advanced Filters</Typography>
      </Modal.Header>
      <Modal.Body>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Form.Control>
            <Form.Label>Status</Form.Label>
            <Select
              placeholder="Select status"
              value={status}
              onChange={(value) => setStatus(value as string[])}
              options={statusOptions}
              multiple
              searchable
            />
          </Form.Control>

          <Form.Control>
            <Form.Label>Scheduled For</Form.Label>
            <div className="w-fit">
              <DualDateRangePicker
                value={{
                  from: dateRange.startDate,
                  to: dateRange.endDate,
                }}
                onChange={(value) =>
                  setDateRange({
                    startDate: value?.from ?? null,
                    endDate: value?.to ?? null,
                  })
                }
              />
            </div>
          </Form.Control>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex gap-3">
          <Button variant="outline" onClick={handleReset}>
            Reset
          </Button>
          <Button variant="primary" onClick={handleFilter}>
            Filter
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
